import { useState, useRef, useEffect } from 'react'

// Shared "live visitor location" tracking, used by every tool that walks a
// person to a stone (public Search, field tool search mode). Consolidates
// what used to be three copies of watchPosition/clearWatch bookkeeping — one
// of which (the field tool) had no stop button at all.
export function useVisitorTracking() {
  const [visitorLocation, setVisitorLocation] = useState(null)
  const [isTracking, setIsTracking] = useState(false)
  const watchIdRef = useRef(null)
  const timeoutRef = useRef(null)

  const clearTimers = () => {
    if (watchIdRef.current !== null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null }
    if (timeoutRef.current !== null) { clearTimeout(timeoutRef.current); timeoutRef.current = null }
  }

  useEffect(() => clearTimers, [])

  const startTracking = () => {
    if (watchIdRef.current !== null) return
    setIsTracking(true)
    let gotFix = false
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        gotFix = true
        if (timeoutRef.current !== null) { clearTimeout(timeoutRef.current); timeoutRef.current = null }
        setVisitorLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      (err) => {
        // watchPosition's own `timeout` option is unreliable in Safari/WebKit —
        // it often never fires, leaving this stuck at "Locating..." forever.
        // The manual setTimeout below is the real failsafe; only treat
        // PERMISSION_DENIED as fatal here so a routine transient error mid-walk
        // doesn't kill tracking outright.
        if (err.code === 1) {
          clearTimers()
          setIsTracking(false)
          alert('Could not get your location. Please enable location access.')
        }
      },
      { enableHighAccuracy: true, timeout: 15000 }
    )
    timeoutRef.current = setTimeout(() => {
      if (gotFix) return
      clearTimers()
      setIsTracking(false)
      alert('Could not get your location — your device may not have a GPS/location fix right now. Try again in a moment.')
    }, 15000)
  }

  const stopTracking = () => {
    clearTimers()
    setIsTracking(false)
    setVisitorLocation(null)
  }

  return { visitorLocation, isTracking, startTracking, stopTracking }
}
